import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Buildings, Truck, UserCircle, WarningCircle, Gift } from "@phosphor-icons/react";

export default function Register() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [role, setRole] = useState("company"); // "company" | "motorista"
  const [motoristaSubtype, setMotoristaSubtype] = useState("autonomo"); // "autonomo" | "empregado_sem_empresa" | "empregado_com_empresa"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [companyNotFoundOpen, setCompanyNotFoundOpen] = useState(false);
  const [missingCompanyEmail, setMissingCompanyEmail] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [source, setSource] = useState("");  // Como conheceu?
  const [sourceCampaignId, setSourceCampaignId] = useState("");
  const [sourceDetail, setSourceDetail] = useState("");
  const [campaignsPublic, setCampaignsPublic] = useState([]);

  useEffect(() => {
    api.get("/marketing/campaigns/public").then((r) => setCampaignsPublic(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const empresa = params.get("empresa");
    const ref = params.get("ref");
    const emailPrefill = params.get("email");
    if (empresa) {
      setRole("motorista");
      setMotoristaSubtype("empregado_com_empresa");
      setForm((prev) => ({ ...prev, company_email: empresa }));
    }
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setForm((prev) => ({ ...prev, referral_code: ref.toUpperCase() }));
    }
    if (emailPrefill) {
      setForm((prev) => ({ ...prev, email: emailPrefill }));
    }
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let endpoint;
      let payload = { ...form };
      let redirect = "/empresa";

      if (role === "company") {
        endpoint = "/auth/register/company";
      } else if (motoristaSubtype === "empregado_com_empresa") {
        endpoint = "/auth/register/driver";
        redirect = "/motorista";
      } else {
        // autonomo OR empregado sem empresa cadastrada
        endpoint = "/auth/register/autonomo";
        payload.driver_mode = motoristaSubtype === "empregado_sem_empresa" ? "empregado" : "autonomo";
        redirect = "/autonomo";
      }
      if (referralCode) payload.referral_code = referralCode;
      if (source) {
        payload.source = source;
        if (source === "flyer" && sourceCampaignId) payload.source_campaign_id = sourceCampaignId;
        if (source === "other" && sourceDetail) payload.source_detail = sourceDetail;
      }
      const { data } = await api.post(endpoint, payload);
      login(data.token, data.user);
      toast.success("Cadastro realizado! Bem-vindo.");
      nav(redirect);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Erro no cadastro";
      const isCompanyNotFound =
        role === "motorista" &&
        motoristaSubtype === "empregado_com_empresa" &&
        err.response?.status === 404 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("empresa não encontrada");
      if (isCompanyNotFound) {
        setMissingCompanyEmail(form.company_email || "");
        setCompanyNotFoundOpen(true);
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const goCreateCompany = () => {
    setCompanyNotFoundOpen(false);
    setRole("company");
    setForm({});
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const SubtypeRadio = ({ value, label, description, testid }) => (
    <label
      className={`flex items-start gap-3 border-2 rounded-lg p-3 cursor-pointer transition-colors ${motoristaSubtype === value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}
      data-testid={testid}
    >
      <input
        type="radio"
        name="motorista_subtype"
        value={value}
        checked={motoristaSubtype === value}
        onChange={() => setMotoristaSubtype(value)}
        className="mt-1 accent-blue-600"
      />
      <div>
        <div className="font-bold text-slate-900 text-sm">{label}</div>
        <div className="text-xs text-slate-600 mt-0.5">{description}</div>
      </div>
    </label>
  );

  const SourceBlock = () => (
    <div className="rounded-lg border-2 border-fuchsia-200 bg-fuchsia-50/40 p-3 space-y-2">
      <Label className="text-xs uppercase tracking-wider text-fuchsia-800 font-bold">Como você conheceu a empresa?</Label>
      <select
        data-testid="reg-source"
        value={source}
        onChange={(e) => { setSource(e.target.value); setSourceCampaignId(""); setSourceDetail(""); }}
        className="w-full border-2 rounded-md h-10 px-2 bg-white text-sm"
      >
        <option value="">-- selecione --</option>
        <option value="flyer">🚚 Panfleto no posto</option>
        <option value="friend">👥 Indicação de amigo</option>
        <option value="instagram">📷 Instagram</option>
        <option value="facebook">👍 Facebook</option>
        <option value="google">🔍 Pesquisa no Google</option>
        <option value="other">🤷 Outra forma</option>
      </select>
      {source === "flyer" && (
        <select
          data-testid="reg-source-campaign"
          value={sourceCampaignId}
          onChange={(e) => setSourceCampaignId(e.target.value)}
          className="w-full border-2 rounded-md h-10 px-2 bg-white text-sm"
        >
          <option value="">-- em qual posto você pegou o panfleto? --</option>
          {campaignsPublic.map((c) => (
            <option key={c.id} value={c.id}>{c.posto} — {c.city}/{c.uf}</option>
          ))}
          <option value="">Não lembro / outro posto</option>
        </select>
      )}
      {source === "other" && (
        <Input
          data-testid="reg-source-detail"
          placeholder="Conta pra gente como você chegou aqui..."
          value={sourceDetail}
          onChange={(e) => setSourceDetail(e.target.value)}
          className="bg-white"
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 grid-bg">
      <Card className="w-full max-w-xl p-6 md:p-8 border-2 shadow-lg">
        <div className="mb-4 flex justify-center"><Logo size={52} /></div>
        <h2 className="text-3xl font-extrabold tracking-tight text-center" style={{ fontFamily: "Manrope" }}>Criar conta</h2>
        <p className="text-sm text-muted-foreground text-center mt-1">7 dias grátis, sem cartão</p>

        {referralCode && (
          <div data-testid="referral-banner" className="mt-4 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 flex items-center gap-2">
            <Gift size={20} weight="fill" className="text-emerald-600" />
            <div className="text-sm">
              <strong className="text-emerald-800">Você foi indicado!</strong>
              <span className="text-emerald-700"> Ganhará <strong>50% OFF</strong> no seu 2º pagamento. Código: <code className="bg-white px-1 rounded">{referralCode}</code></span>
            </div>
          </div>
        )}

        <Tabs value={role} onValueChange={setRole} className="mt-6">
          <TabsList className="grid grid-cols-2 w-full h-auto p-1">
            <TabsTrigger value="company" data-testid="reg-tab-company" className="py-2.5 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Buildings size={16} weight="duotone" /> Empresa
            </TabsTrigger>
            <TabsTrigger value="motorista" data-testid="reg-tab-motorista" className="py-2.5 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Truck size={16} weight="duotone" /> Motorista
            </TabsTrigger>
          </TabsList>

          <TabsContent value="company">
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div><Label className="text-xs uppercase tracking-wider">Nome da empresa</Label>
                <Input data-testid="reg-company-name" required onChange={set("company_name")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider">CNPJ</Label>
                  <Input data-testid="reg-cnpj" onChange={set("cnpj")} /></div>
                <div><Label className="text-xs uppercase tracking-wider">Telefone</Label>
                  <Input data-testid="reg-phone-company" onChange={set("phone")} /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider">E-mail</Label>
                <Input data-testid="reg-email-company" type="email" required value={form.email || ""} onChange={set("email")} /></div>
              <div><Label className="text-xs uppercase tracking-wider">Senha</Label>
                <Input data-testid="reg-password-company" type="password" required onChange={set("password")} /></div>
              <SourceBlock />
              <Button type="submit" data-testid="reg-submit-company" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 hover:text-white text-white h-11">
                {busy ? "Criando..." : "Cadastrar Empresa"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="motorista">
            <div className="mt-6 space-y-3">
              <Label className="text-xs uppercase tracking-wider text-blue-800 font-bold">Que tipo de motorista você é?</Label>
              <SubtypeRadio
                value="autonomo"
                label="🚛 Autônomo (dono do próprio caminhão)"
                description="Freteiro, agregado ou caminhoneiro por conta própria. Você gerencia seus próprios fretes."
                testid="subtype-autonomo"
              />
              <SubtypeRadio
                value="empregado_com_empresa"
                label="👷 Empregado — minha empresa JÁ usa o app"
                description="Vincule-se à empresa que já tem conta (peça o e-mail dela)."
                testid="subtype-empregado-com"
              />
              <SubtypeRadio
                value="empregado_sem_empresa"
                label="👷 Empregado — quero usar sozinho + convidar minha empresa"
                description="Sua empresa ainda não usa o app. Você controla seu acerto individualmente e pode indicá-la pra ganhar 50% OFF."
                testid="subtype-empregado-sem"
              />
            </div>

            <form onSubmit={submit} className="space-y-4 mt-6">
              <div><Label className="text-xs uppercase tracking-wider">Nome completo</Label>
                <Input data-testid="reg-driver-name" required onChange={set("name")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider">CPF</Label>
                  <Input data-testid="reg-cpf" onChange={set("cpf")} /></div>
                <div><Label className="text-xs uppercase tracking-wider">Telefone</Label>
                  <Input data-testid="reg-phone-driver" onChange={set("phone")} /></div>
              </div>

              {motoristaSubtype === "empregado_com_empresa" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">E-mail da empresa</Label>
                  <Input data-testid="reg-company-email" type="email" required value={form.company_email || ""} onChange={set("company_email")} placeholder="empresa@exemplo.com" />
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mt-1.5">
                    <strong>Importante:</strong> este é o e-mail que <u>sua empresa usou ao criar a conta</u>.
                  </p>
                </div>
              )}

              {motoristaSubtype === "empregado_sem_empresa" && (
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3 space-y-3">
                  <div className="text-xs font-bold text-emerald-800 flex items-center gap-1"><Gift size={14} weight="fill" /> Ganhe 50% OFF indicando sua empresa</div>
                  <div>
                    <Label className="text-xs">Nome da empresa em que você trabalha (opcional agora)</Label>
                    <Input data-testid="reg-employer-name" onChange={set("employer_name")} placeholder="Ex: Transportadora Silva" />
                  </div>
                  <div>
                    <Label className="text-xs">Telefone/WhatsApp da empresa (pra convidar depois)</Label>
                    <Input data-testid="reg-employer-phone" onChange={set("employer_phone")} placeholder="(11) 99999-9999" />
                  </div>
                  <p className="text-xs text-emerald-700">Depois do cadastro, você recebe um link único pra convidar sua empresa. Se ela assinar, você ganha <strong>50% OFF</strong> — até 3 indicações válidas.</p>
                </div>
              )}

              <div><Label className="text-xs uppercase tracking-wider">Seu e-mail</Label>
                <Input data-testid="reg-email-driver" type="email" required value={form.email || ""} onChange={set("email")} /></div>
              <div><Label className="text-xs uppercase tracking-wider">Senha</Label>
                <Input data-testid="reg-password-driver" type="password" required onChange={set("password")} /></div>
              <SourceBlock />
              <Button type="submit" data-testid="reg-submit-driver" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 hover:text-white text-white h-11">
                {busy ? "Criando..." : "Cadastrar Motorista"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <p className="text-sm text-center text-muted-foreground mt-6">
          Já tem conta?{" "}
          <Link to="/login" data-testid="link-login" className="text-blue-700 font-semibold hover:underline">Entrar</Link>
        </p>
        <p className="text-[10px] text-center text-slate-500 mt-3">
          Ao criar conta você concorda com nossos <Link to="/termos" className="underline hover:text-blue-700">Termos de Uso</Link> e <Link to="/privacidade" className="underline hover:text-blue-700">Política de Privacidade (LGPD)</Link>.
        </p>
      </Card>

      <Dialog open={companyNotFoundOpen} onOpenChange={setCompanyNotFoundOpen}>
        <DialogContent data-testid="company-not-found-modal" className="max-w-md border-2 border-amber-300">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-2">
              <WarningCircle size={40} weight="fill" className="text-amber-600" />
            </div>
            <DialogTitle className="text-2xl text-center" style={{ fontFamily: "Manrope" }}>
              Empresa ainda não cadastrada
            </DialogTitle>
            <DialogDescription className="text-center text-slate-700 pt-2">
              Não encontramos empresa com o e-mail{" "}
              {missingCompanyEmail && <strong className="text-slate-900 break-all">"{missingCompanyEmail}"</strong>}.
              <br /><br />
              Duas opções:
              <br />1) Continuar como <strong>Empregado — usar sozinho</strong> (você controla seu acerto e pode indicar a empresa depois)
              <br />2) Pedir à empresa pra se cadastrar primeiro
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              data-testid="btn-switch-to-solo"
              onClick={() => { setCompanyNotFoundOpen(false); setMotoristaSubtype("empregado_sem_empresa"); }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white h-11"
            >
              <Truck size={18} weight="duotone" className="mr-2" /> Usar sozinho + indicar minha empresa
            </Button>
            <Button
              data-testid="btn-go-register-company"
              onClick={goCreateCompany}
              variant="outline"
              className="w-full h-11"
            >
              <Buildings size={18} weight="duotone" className="mr-2" /> Sou o dono — cadastrar Empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
